import { mutate } from "swr";

/**
 * Generic fetcher function for SWR that includes caching
 * @param {string} url - The URL to fetch data from
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} The parsed JSON response
 */
export const fetcher = async (url, options = {}) => {

  const response = await fetch(url, options);

  if (!response.ok) {
    const error = new Error('An error occurred while fetching the data.');
    error.info = await response.json();
    error.status = response.status;

    throw error;
  }

  const data = await response.json();

  // Update the SWR cache
  mutate(url, data, false);

  return data;
};

/**
 * Generates a consistent cache key for product listings
 * @param {number} page - Page number
 * @param {number} categoryId - Category ID
 * @param {number} perPage - Items per page
 * @param {string} orderby - Order by field
 * @param {string} order - Sort direction
 * @returns {string} The cache key
 */
export const getProductsKey = (page, categoryId, perPage = 10, orderby = 'title', order = 'asc', status = 'publish') =>
  `/api/products?page=${page}&perPage=${perPage}&category=${categoryId}&orderby=${orderby}&order=${order}&status=${status}`;

/**
 * Generates a cache key for on-sale products
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 * @returns {string} The cache key
 */
export const getOnSaleProductsKey = (page, perPage = 10) =>
  `/api/shop/on-sale?page=${page}&perPage=${perPage}`;

/**
 * Generates a cache key for fetching products by IDs (used for upsell/cross-sell)
 * @param {Array<number>} productIds - Array of product IDs to fetch
 * @returns {string} The cache key
 */
export const getProductsByIdsKey = (productIds) => {
  if (!productIds || !productIds.length) return null;
  return `/api/products?include=${productIds.join(',')}`;
};

/**
 * Cache key for product tags
 * @param {Object} params - Additional parameters for the tags API
 * @returns {string} The cache key
 */
export const getProductTagsKey = (params = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  return `/api/shop/product/tags${queryString ? `?${queryString}` : ''}`;
};

/**
 * Cache key for shipping slots
 * @param {string} address - Delivery address to check
 * @returns {string|null} The cache key or null if no address provided
 */
export const getShippingSlotsKey = (address, forceRefresh = false) => {
  if (!address) return null;

  const baseUrl = process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL;
  const encodedAddress = encodeURIComponent(address);
  let fullUrl = `${baseUrl}wp-json/beaver-next-shipping-area/v1/check-address?address=${encodedAddress}`;

  // Add timestamp to force fresh requests if needed
  if (forceRefresh) {
    fullUrl += `&_t=${Date.now()}`;
  }

  return fullUrl;
};

/**
 * Cache key for shipping methods by country
 * @param {string} country - Country code
 * @returns {string|null} The cache key or null if no country provided
 */
export const getShippingMethodsKey = (country) => country ? `/api/shop/shipping/methods?country=${country}` : null;

/**
 * Cache key for payment methods
 * @returns {string|null} The cache key or null if no token available
 */
export const getPaymentMethodsKey = () => localStorage.getItem('token') ? `/api/shop/payment/methods` : null;